import { Guide, GuideRole } from './types';

const AVAILABILITY_ROUTE = '/member/availability';

const goToAvailability = {
  target: 'nav-availability',
  route: AVAILABILITY_ROUTE,
  title: 'Open Availability',
  body: 'Your calendar lives here. Click Availability in the menu.',
  advanceOn: 'click' as const,
};

/**
 * Every guided walkthrough the Help panel can offer, keyed to the
 * `data-guide` anchors placed on real controls in the app.
 */
export const GUIDES: Guide[] = [
  {
    id: 'add-availability',
    title: 'Add availability',
    description: 'Open up hours that clients can book.',
    roles: ['member'],
    steps: [
      goToAvailability,
      {
        target: 'add-availability',
        title: 'Add availability',
        body: 'Click here. You can also drag on an empty part of the calendar to pick the hours directly.',
        advanceOn: 'click',
      },
      {
        target: 'create-times',
        title: 'Pick the hours',
        body: 'Choose when you start and finish. Times are in 15-minute steps inside clinic hours.',
      },
      {
        target: 'create-repeat',
        title: 'Repeat it?',
        body: '"Once" is a single day. "Every week" and "Every 2 weeks" fill in the same hours for the next 6 months.',
      },
      {
        target: 'create-submit',
        title: 'Save',
        body: 'Click Add availability. This creates the real availability, and the new hours show up in green on your calendar.',
        placement: 'top',
        advanceOn: 'click',
      },
    ],
  },
  {
    id: 'reserve-room',
    title: 'Reserve a room for myself',
    description: 'Book part of your open availability for your own session.',
    roles: ['member'],
    steps: [
      goToAvailability,
      {
        target: 'availability-block',
        title: 'Click a green block',
        body: 'Green blocks are your open availability. Click the one that contains the time you need.',
        advanceOn: 'click',
      },
      {
        target: 'edit-tab-reserve',
        title: 'Choose "Reserve for me"',
        body: 'This tab books a room for your own use.',
        advanceOn: 'click',
      },
      {
        target: 'selfbook-window',
        title: 'Pick the exact time',
        body: 'Set when the booking starts and how long it runs. Only that window is booked and billed. The rest of the block stays open for clients.',
      },
      {
        target: 'selfbook-submit',
        title: 'Reserve',
        body: 'Click Reserve to see the summary.',
        placement: 'top',
        advanceOn: 'click',
      },
      {
        target: 'selfbook-confirm',
        title: 'Confirm',
        body: 'Check the time, then click Confirm booking. This is a real booking. If it\'s a mistake, cancel it from My Bookings, which shows exactly how long you have to do that at no charge.',
        placement: 'top',
        advanceOn: 'click',
      },
    ],
  },
  {
    id: 'remove-availability',
    title: 'Remove availability',
    description: 'Take back open hours, including around an existing booking.',
    roles: ['member'],
    steps: [
      goToAvailability,
      {
        target: 'availability-block',
        title: 'Click the block',
        body: 'Click the availability you want to remove. If part of it is booked, click the open part next to the booking.',
        advanceOn: 'click',
      },
      {
        target: 'edit-tab-remove',
        title: 'Choose "Remove"',
        body: 'The Remove tab lists what you can take away.',
        advanceOn: 'click',
      },
      {
        target: 'remove-scope',
        title: 'Pick how much',
        body: 'Just this block, everything on that day, or this time slot every week going forward. Booked time is always kept.',
      },
      {
        target: 'remove-submit',
        title: 'Confirm',
        body: 'Click the red button. This can\'t be undone.',
        placement: 'top',
        advanceOn: 'click',
      },
    ],
  },
  {
    id: 'clear-availability',
    title: 'Clear many days at once',
    description: 'Wipe a week, a month, or everything from today onward.',
    roles: ['member'],
    steps: [
      goToAvailability,
      {
        target: 'clear-availability',
        title: 'Clear Availability',
        body: 'Use this instead of deleting blocks one by one.',
        advanceOn: 'click',
      },
      {
        target: 'clear-presets',
        title: 'Choose the range',
        body: 'This week, this month, everything from today, or dates you pick. Booked time and breaks are never touched.',
      },
      {
        target: 'clear-submit',
        title: 'Confirm',
        body: 'Tick the checkbox, then click Clear availability.',
        placement: 'top',
        advanceOn: 'click',
      },
    ],
  },
  {
    id: 'add-break',
    title: 'Add a break',
    description: 'Block off lunch or time away so clients can\'t book it.',
    roles: ['member'],
    steps: [
      goToAvailability,
      {
        target: 'add-break',
        title: 'Add Break',
        body: 'Click here to block off time inside your availability.',
        advanceOn: 'click',
      },
      {
        target: 'break-form',
        title: 'Set the break',
        body: 'Pick the day and times. Make it weekly if it\'s a regular lunch. Upcoming breaks are listed below the form.',
      },
      {
        target: 'break-submit',
        title: 'Save',
        body: 'Click Add break. It appears as a dashed grey block on your calendar.',
        placement: 'top',
        advanceOn: 'click',
      },
    ],
  },
  {
    id: 'book-pod',
    title: 'Book a pod',
    description: 'Reserve an individual workspace at the flat pod rate.',
    roles: ['member'],
    steps: [
      {
        target: 'nav-book-pod',
        route: '/member/book-pod',
        title: 'Open Book a Pod',
        body: 'Pods don\'t need availability. Click Book a Pod in the menu.',
        advanceOn: 'click',
      },
      {
        target: 'pod-form',
        title: 'Pick a time',
        body: 'Choose the date, start time and how long you need. Your existing bookings for that day are shown above so you can avoid a clash.',
      },
      {
        target: 'pod-submit',
        title: 'Reserve',
        body: 'Click Reserve Pod. This is a real booking and the cost is added to your monthly invoice. If it\'s a mistake, cancel it from My Bookings, which shows how long you have to do that at no charge.',
        placement: 'top',
        advanceOn: 'click',
      },
    ],
  },
  {
    id: 'book-appointment',
    title: 'Book an appointment',
    description: 'Find a time with one of your clinicians.',
    roles: ['patient'],
    steps: [
      {
        target: 'nav-book',
        route: '/patient/book',
        title: 'Open Book Appointment',
        body: 'Click Book Appointment in the menu, then pick your clinician and a time that suits you.',
        advanceOn: 'click',
      },
    ],
  },
  {
    id: 'admin-bookings',
    title: 'Manage a booking',
    description: 'Reschedule, change the duration, reassign the room, or cancel.',
    roles: ['admin'],
    steps: [
      {
        target: 'nav-bookings',
        route: '/admin/bookings',
        title: 'Open All Bookings',
        body: 'Every booking is listed here. Use the ⋮ menu on a row to reschedule (including a new duration), reassign the room, resend the confirmation, or cancel.',
        advanceOn: 'click',
      },
    ],
  },
];

export const guidesForRole = (role: GuideRole | undefined): Guide[] =>
  role ? GUIDES.filter(g => g.roles.includes(role)) : [];

export const findGuide = (id: string): Guide | undefined => GUIDES.find(g => g.id === id);
